<template>
  <div class="sheet-overlay" @click.self="$emit('close')">
    <div class="sheet-container">
      <!-- 핸들바 -->
      <div class="sheet-handle">
        <div class="handle-bar"></div>
      </div>

      <!-- 헤더 -->
      <div class="sheet-header">
        <button class="back-btn" @click="$emit('close')">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <h2 class="sheet-title">상담사 선택</h2>
        <div class="w-10"></div>
      </div>

      <!-- 안내 문구 -->
      <div class="guide-section">
        <p class="guide-text">
          <strong>{{ property.name }}</strong> 현장의 상담사 중<br/>
          한 분을 선택해주세요
        </p>
      </div>

      <!-- 상담사 목록 -->
      <div class="sheet-content">
        <!-- 점프 중인 상담사 섹션 -->
        <div v-if="jumpingPartners.length > 0" class="jump-section">
          <div class="jump-section-header">
            <svg class="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
            </svg>
            <span class="text-sm font-semibold text-violet-600">추천 상담사</span>
          </div>
          <div class="partners-list">
            <div
              v-for="partner in jumpingPartners"
              :key="'jump-' + partner.id"
              class="partner-card partner-card-jumping"
              @click="selectPartner(partner)"
            >
              <div class="jump-badge">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
                JUMP
              </div>
              <div class="partner-avatar partner-avatar-jumping">
                {{ partner.displayName?.charAt(0) || '?' }}
              </div>
              <div class="partner-info">
                <h4 class="partner-name partner-name-jumping">{{ partner.displayName }}</h4>
                <p class="partner-intro partner-intro-jumping">{{ partner.introduction || '안녕하세요! 정성껏 상담해드리겠습니다.' }}</p>
              </div>
              <div class="select-indicator">
                <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- 일반 상담사 섹션 -->
        <div v-if="normalPartners.length > 0" class="normal-section">
          <div v-if="jumpingPartners.length > 0" class="normal-section-header">
            <span class="text-sm font-medium text-slate-500">다른 상담사</span>
          </div>
          <div class="partners-list">

          <div
            v-for="partner in normalPartners"
            :key="partner.id"
            class="partner-card"
            @click="selectPartner(partner)"
          >
            <div class="partner-avatar">
              {{ partner.displayName?.charAt(0) || '?' }}
            </div>
            <div class="partner-info">
              <h4 class="partner-name">{{ partner.displayName }}</h4>
              <p class="partner-intro">{{ partner.introduction || '안녕하세요! 정성껏 상담해드리겠습니다.' }}</p>
            </div>
            <div class="select-indicator">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
          </div>
        </div>

        <!-- 빠른 상담 옵션 -->
        <div class="quick-consult-section">
          <div class="divider">
            <span>또는</span>
          </div>
          <button class="quick-consult-btn" @click="selectQuickConsult">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            빠른 상담 신청
            <span class="quick-consult-desc">선착순으로 상담사가 배정됩니다</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export default {
  name: 'ConsultantSelectSheet',
  props: {
    property: {
      type: Object,
      required: true
    },
    partners: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'select'],
  setup(props, { emit }) {
    const activeJumpPartnerIds = ref([])

    // 한국 시간(KST) 가져오기
    const getKoreanTime = () => {
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      const year = koreaTime.getFullYear()
      const month = String(koreaTime.getMonth() + 1).padStart(2, '0')
      const day = String(koreaTime.getDate()).padStart(2, '0')
      const hours = String(koreaTime.getHours()).padStart(2, '0')
      const minutes = String(koreaTime.getMinutes()).padStart(2, '0')

      return {
        date: `${year}-${month}-${day}`,
        time: `${hours}:${minutes}`
      }
    }

    // 현재 점프 중인 파트너 ID 조회
    const loadActiveJumps = async () => {
      try {
        const { date: today, time: currentTime } = getKoreanTime()

        const q = query(
          collection(db, 'jumpSlots'),
          where('propertyId', '==', props.property.id),
          where('status', '==', 'active')
        )

        const snapshot = await getDocs(q)
        const jumpingIds = []

        snapshot.docs.forEach(doc => {
          const data = doc.data()

          // 현재 날짜가 점프 기간 내인지 확인
          if (data.startDate <= today && data.endDate >= today) {
            // timeSlot (단수, 객체)가 있는지 확인
            if (data.timeSlot && data.timeSlot.start && data.timeSlot.end) {
              // 현재 시간이 점프 시간대 내인지 확인
              const inSlot = currentTime >= data.timeSlot.start && currentTime < data.timeSlot.end
              if (inSlot) {
                jumpingIds.push(data.partnerId)
              }
            }
          }
        })

        activeJumpPartnerIds.value = jumpingIds
      } catch (err) {
        console.error('활성 점프 조회 실패:', err)
      }
    }

    // 점프 중인 파트너들
    const jumpingPartners = computed(() => {
      return props.partners.filter(p =>
        activeJumpPartnerIds.value.includes(p.partnerId)
      )
    })

    // 일반 파트너들
    const normalPartners = computed(() => {
      return props.partners.filter(p =>
        !activeJumpPartnerIds.value.includes(p.partnerId)
      )
    })

    const selectPartner = (partner) => {
      emit('select', partner)
    }

    const selectQuickConsult = () => {
      // 빠른 상담: 첫 번째 파트너 또는 랜덤 선택
      if (props.partners.length > 0) {
        const randomIndex = Math.floor(Math.random() * props.partners.length)
        emit('select', {
          ...props.partners[randomIndex],
          isQuickConsult: true
        })
      }
    }

    onMounted(() => {
      loadActiveJumps()
    })

    return {
      jumpingPartners,
      normalPartners,
      selectPartner,
      selectQuickConsult
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

/* 모바일: 바텀시트 */
.sheet-overlay {
  @apply fixed inset-0 z-50 bg-black/50 flex items-end justify-center;
  animation: fadeIn 0.2s ease-out;
  padding-bottom: 64px; /* MobileTabBar 높이 */
}

.sheet-container {
  @apply w-full max-h-[85vh] bg-white rounded-t-3xl flex flex-col;
  animation: slideUp 0.3s ease-out;
}

/* 데스크탑: 중앙 모달 */
@media (min-width: 769px) {
  .sheet-overlay {
    @apply items-center;
    padding-bottom: 0;
  }

  .sheet-container {
    @apply max-w-md rounded-2xl;
    max-height: 80vh;
    animation: scaleIn 0.2s ease-out;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.sheet-handle {
  @apply flex justify-center py-3;
}

.handle-bar {
  @apply w-10 h-1 bg-slate-300 rounded-full;
}

.sheet-header {
  @apply flex items-center justify-between px-4 pb-3;
}

.back-btn {
  @apply p-2 text-slate-600 hover:bg-slate-100 rounded-full;
}

.sheet-title {
  @apply text-lg font-semibold text-slate-800;
}

.guide-section {
  @apply px-5 pb-4 border-b border-slate-100;
}

.guide-text {
  @apply text-center text-slate-600 leading-relaxed;
}

.sheet-content {
  @apply flex-1 overflow-y-auto p-5;
}

.partners-list {
  @apply space-y-3;
}

.partner-card {
  @apply flex items-center gap-4 p-4 bg-slate-50 rounded-2xl cursor-pointer transition hover:bg-slate-100 active:scale-98 relative;
}

.jump-section {
  @apply mb-4;
}

.jump-section-header {
  @apply flex items-center gap-1.5 mb-3 px-1;
}

.normal-section-header {
  @apply mb-3 px-1 pt-2;
}

.partner-card-jumping {
  @apply bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-300 shadow-sm shadow-violet-100;
}

.partner-card-jumping:hover {
  @apply from-violet-100 to-purple-100;
}

.jump-badge {
  @apply absolute -top-2.5 left-4 px-2.5 py-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-md;
}

.partner-name-jumping {
  @apply text-violet-700;
}

.partner-intro-jumping {
  @apply text-violet-500;
}

.partner-avatar {
  @apply w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xl font-bold flex-shrink-0;
}

.partner-avatar-jumping {
  @apply from-violet-600 to-purple-700;
}

.partner-info {
  @apply flex-1 min-w-0;
}

.partner-name {
  @apply text-base font-semibold text-slate-800;
}

.partner-intro {
  @apply text-sm text-slate-500 mt-0.5 line-clamp-2;
}

.select-indicator {
  @apply flex-shrink-0;
}

.quick-consult-section {
  @apply mt-6;
}

.divider {
  @apply flex items-center gap-4 mb-4;
}

.divider::before,
.divider::after {
  @apply flex-1 h-px bg-slate-200;
  content: '';
}

.divider span {
  @apply text-sm text-slate-400;
}

.quick-consult-btn {
  @apply w-full flex flex-col items-center justify-center py-4 bg-amber-50 text-amber-700 font-semibold rounded-xl border-2 border-dashed border-amber-200 hover:bg-amber-100 transition;
}

.quick-consult-btn svg {
  @apply mb-1;
}

.quick-consult-desc {
  @apply text-xs font-normal text-amber-500 mt-1;
}
</style>
